import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Spinner, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import { Action, ItemType, Page } from '@/../../server/types/loggingTypes';
import chevronLeft from '@/assets/chevron_left.svg';
import logo from '@/assets/logo.png';
import OTPInput, { OTPInputRef } from '@/components/PhoneVerification/OTPInput';
import ResendOTP, { OTPType } from '@/components/PhoneVerification/ResendOTP';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { signupEmailAtom } from '@/store/signupAtoms';
import { setRefreshToken } from '@/utils/auth';
import { getStatsigClient } from '@/utils/statsig';

export const Route = createFileRoute('/signup/email-verification')({
  component: EmailVerificationPage,
});

function EmailVerificationPage() {
  const email = useAtomValue(signupEmailAtom);
  const navigate = useNavigate({ from: '/signup/email-verification' });
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const otpInputRef = useRef<OTPInputRef>(null);
  const { back } = useRouterNavigation();

  // Redirect to signup if email not available
  useEffect(() => {
    if (!email) {
      navigate({ to: '/signup' });
    }
  }, [email, navigate]);

  const handleResend = useCallback(async () => {
    getStatsigClient().logEvent(Page.LANDING_PAGE, Action.CLICK, {
      item_name: 'resend_otp',
      item_type: ItemType.BUTTON,
    });
    try {
      await axios.post('/api/auth/sendEmailVerificationCode', { email });
      otpInputRef.current?.reset();
    } catch (error) {
      toast({ title: 'Failed to resend code.', status: 'error', duration: 3000 });
    }
  }, [email, toast]);

  const handleOtpComplete = useCallback(
    async (otp: string) => {
      setIsLoading(true);
      getStatsigClient().logEvent('web_verify_email_code');
      try {
        const res = await axios.post('/api/auth/verifyEmailCode', {
          email,
          code: otp,
        });

        const { sessionToken, refreshToken } = res.data;
        localStorage.setItem('sessionToken', sessionToken);
        setRefreshToken(refreshToken);

        getStatsigClient().logEvent('web_verify_email_code_success');

        window.location.href = '/';
      } catch (error: any) {
        console.error(error);
        getStatsigClient().logEvent('web_verify_email_code_error', error);
        toast({ title: 'Invalid code, please try again.', status: 'error', duration: 3000 });
      } finally {
        setIsLoading(false);
      }
    },
    [email, toast],
  );

  return (
    <div className="flex flex-col items-center min-h-screen bg-[#121212]">
      {/* Header */}
      <div className="relative flex flex-row items-center justify-center py-3 px-4 w-full">
        <img src={chevronLeft} alt="Back" className="absolute left-4 size-6 cursor-pointer" onClick={back} />
        <img src={logo} alt="Bond Logo" className="h-8 object-contain" />
      </div>

      <h1 className="text-white text-[34px]/[40px] tracking-tight text-center max-w-[280px] font-bold mt-6">
        Verify your email
      </h1>

      <p className="text-[#ffffff60] text-[16px]/[20px] tracking-tight text-center max-w-[280px] mb-10 mt-4">
        A 6 digit code was sent to your email. Didn't get it?{' '}
        <ResendOTP type={OTPType.EMAIL} onResend={handleResend} short />
      </p>

      <OTPInput ref={otpInputRef} onComplete={handleOtpComplete} />

      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/60 z-50">
          <Spinner thickness="4px" speed="0.75s" emptyColor="#1c1c1c" color="#FF0050" size="lg" />
        </div>
      )}
    </div>
  );
}
